// Member App Analytics - Feature Mapping Configuration
// Maps every member app route to a feature name for usage tracking
// This tracks ACTUAL member behavior in the golf app

export interface FeatureMapping {
  route: string;
  featureName: string;
  featureGroup: string;
  description: string;
}

/**
 * Complete mapping of Member App routes to analytics feature names
 * Used by trackPageView() to categorize member app usage
 */
export const MEMBER_FEATURE_MAPPING: FeatureMapping[] = [
  // Dashboard & Home
  {
    route: '/',
    featureName: 'member_home',
    featureGroup: 'navigation',
    description: 'Member app home page'
  },
  {
    route: '/dashboard',
    featureName: 'member_dashboard',
    featureGroup: 'navigation',
    description: 'Main member dashboard with overview'
  },

  // Golf & Scoring
  {
    route: '/scores',
    featureName: 'score_entry',
    featureGroup: 'golf',
    description: 'Enter and submit golf scores'
  },
  {
    route: '/scores/new',
    featureName: 'score_entry_new',
    featureGroup: 'golf',
    description: 'Create new score entry'
  },
  {
    route: '/scores/history',
    featureName: 'score_history',
    featureGroup: 'golf',
    description: 'View personal scoring history'
  },
  {
    route: '/scores/:scoreId',
    featureName: 'score_detail',
    featureGroup: 'golf',
    description: 'View individual round details'
  },
  {
    route: '/rounds',
    featureName: 'rounds_list',
    featureGroup: 'golf',
    description: 'View all golf rounds'
  },
  {
    route: '/rounds/:roundId',
    featureName: 'round_detail',
    featureGroup: 'golf',
    description: 'View specific round details'
  },

  // Performance & Stats
  {
    route: '/handicap',
    featureName: 'handicap_view',
    featureGroup: 'performance',
    description: 'View handicap index and history'
  },
  {
    route: '/stats',
    featureName: 'stats_overview',
    featureGroup: 'performance',
    description: 'View personal golf statistics'
  },
  {
    route: '/stats/trends',
    featureName: 'stats_trends',
    featureGroup: 'performance',
    description: 'View performance trends over time'
  },
  {
    route: '/performance',
    featureName: 'performance_analytics',
    featureGroup: 'performance',
    description: 'Detailed performance analytics'
  },

  // Competition & Leaderboards
  {
    route: '/leaderboard',
    featureName: 'leaderboard',
    featureGroup: 'competition',
    description: 'View club leaderboards and rankings'
  },
  {
    route: '/leaderboards',
    featureName: 'leaderboards_list',
    featureGroup: 'competition',
    description: 'View all available leaderboards'
  },
  {
    route: '/competitions',
    featureName: 'competitions_list',
    featureGroup: 'competition',
    description: 'View active competitions'
  },

  // Events & Tournaments
  {
    route: '/events',
    featureName: 'events_list',
    featureGroup: 'events',
    description: 'View all club events and tournaments'
  },
  {
    route: '/events/:eventId',
    featureName: 'event_detail',
    featureGroup: 'events',
    description: 'View event details and information'
  },
  {
    route: '/events/:eventId/register',
    featureName: 'event_registration',
    featureGroup: 'events',
    description: 'Register for an event'
  },
  {
    route: '/events/:eventId/leaderboard',
    featureName: 'event_leaderboard',
    featureGroup: 'events',
    description: 'View event leaderboard'
  },
  {
    route: '/tournaments',
    featureName: 'tournaments_list',
    featureGroup: 'events',
    description: 'View tournaments'
  },
  {
    route: '/tournaments/:tournamentId',
    featureName: 'tournament_detail',
    featureGroup: 'events',
    description: 'View tournament details'
  },

  // Social & Community
  {
    route: '/social',
    featureName: 'social_feed',
    featureGroup: 'social',
    description: 'View social feed and activity'
  },
  {
    route: '/social/post',
    featureName: 'social_post_create',
    featureGroup: 'social',
    description: 'Create new social post'
  },
  {
    route: '/feed',
    featureName: 'activity_feed',
    featureGroup: 'social',
    description: 'View activity feed'
  },
  {
    route: '/members',
    featureName: 'members_directory',
    featureGroup: 'social',
    description: 'View club members directory'
  },
  {
    route: '/members/:memberId',
    featureName: 'member_profile_view',
    featureGroup: 'social',
    description: 'View another member profile'
  },

  // Communication
  {
    route: '/messages',
    featureName: 'messaging',
    featureGroup: 'communication',
    description: 'View and send messages'
  },
  {
    route: '/messages/:channelId',
    featureName: 'message_channel',
    featureGroup: 'communication',
    description: 'View specific message channel'
  },
  {
    route: '/channels',
    featureName: 'channels_list',
    featureGroup: 'communication',
    description: 'View all communication channels'
  },
  {
    route: '/announcements',
    featureName: 'announcements_view',
    featureGroup: 'communication',
    description: 'View club announcements'
  },
  {
    route: '/notifications',
    featureName: 'notifications',
    featureGroup: 'communication',
    description: 'View notifications'
  },

  // Bookings & Tee Times
  {
    route: '/tee-times',
    featureName: 'tee_times',
    featureGroup: 'bookings',
    description: 'View and book tee times'
  },
  {
    route: '/tee-times/book',
    featureName: 'tee_time_booking',
    featureGroup: 'bookings',
    description: 'Book a tee time'
  },
  {
    route: '/bookings',
    featureName: 'bookings_list',
    featureGroup: 'bookings',
    description: 'View my bookings'
  },
  {
    route: '/bookings/:bookingId',
    featureName: 'booking_detail',
    featureGroup: 'bookings',
    description: 'View booking details'
  },

  // Course Info
  {
    route: '/courses',
    featureName: 'courses_list',
    featureGroup: 'course_info',
    description: 'View golf courses'
  },
  {
    route: '/courses/:courseId',
    featureName: 'course_detail',
    featureGroup: 'course_info',
    description: 'View course details and layout'
  },
  {
    route: '/course-guide',
    featureName: 'course_guide',
    featureGroup: 'course_info',
    description: 'Interactive course guide'
  },

  // Profile & Account
  {
    route: '/profile',
    featureName: 'profile',
    featureGroup: 'account',
    description: 'View and edit personal profile'
  },
  {
    route: '/profile/edit',
    featureName: 'profile_edit',
    featureGroup: 'account',
    description: 'Edit profile information'
  },
  {
    route: '/settings',
    featureName: 'settings',
    featureGroup: 'account',
    description: 'App settings and preferences'
  },
  {
    route: '/account',
    featureName: 'account_settings',
    featureGroup: 'account',
    description: 'Account settings and management'
  },

  // Club Info
  {
    route: '/club',
    featureName: 'club_info',
    featureGroup: 'club',
    description: 'View club information'
  },
  {
    route: '/club/about',
    featureName: 'club_about',
    featureGroup: 'club',
    description: 'About the club'
  },
  {
    route: '/club/contact',
    featureName: 'club_contact',
    featureGroup: 'club',
    description: 'Club contact information'
  },

  // Help & Support
  {
    route: '/help',
    featureName: 'help',
    featureGroup: 'support',
    description: 'Help and support'
  },
  {
    route: '/faq',
    featureName: 'faq',
    featureGroup: 'support',
    description: 'Frequently asked questions'
  },

  // Play & Golf Rounds
  {
    route: '/play',
    featureName: 'play_home',
    featureGroup: 'golf',
    description: 'Play home — recent courses and round options'
  },
  {
    route: '/play/add-past-round',
    featureName: 'play_add_past_round',
    featureGroup: 'golf',
    description: 'Add a past round manually'
  },
  {
    route: '/play/clubs',
    featureName: 'play_browse_clubs',
    featureGroup: 'golf',
    description: 'Browse clubs to play at'
  },
  {
    route: '/play/solo',
    featureName: 'play_solo_start',
    featureGroup: 'golf',
    description: 'Start a solo round'
  },
  {
    route: '/play/solo/club/:clubId',
    featureName: 'play_solo_course_select',
    featureGroup: 'golf',
    description: 'Select course for solo round'
  },
  {
    route: '/play/solo/:id/celebration',
    featureName: 'play_solo_celebration',
    featureGroup: 'golf',
    description: 'Solo round completion celebration screen'
  },
  {
    route: '/play/solo/:id/complete',
    featureName: 'play_solo_complete',
    featureGroup: 'golf',
    description: 'Solo round completion summary'
  },
  {
    route: '/play/multiplayer/create',
    featureName: 'play_multiplayer_create',
    featureGroup: 'golf',
    description: 'Create a multiplayer game'
  },
  {
    route: '/play/multiplayer/:id/lobby',
    featureName: 'play_multiplayer_lobby',
    featureGroup: 'golf',
    description: 'Multiplayer game lobby'
  },
  {
    route: '/play/multiplayer/:id/score-preview',
    featureName: 'play_multiplayer_score_preview',
    featureGroup: 'golf',
    description: 'Multiplayer score preview'
  },
  {
    route: '/play/multiplayer/:id/complete',
    featureName: 'play_multiplayer_complete',
    featureGroup: 'golf',
    description: 'Multiplayer game completion summary'
  },
  {
    route: '/play/multiplayer/join',
    featureName: 'play_multiplayer_join',
    featureGroup: 'golf',
    description: 'Join a multiplayer game'
  },

  // Coaching & Practice
  {
    route: '/coaching',
    featureName: 'coaching_home',
    featureGroup: 'coaching',
    description: 'Player coaching home — lessons and exercises'
  },
  {
    route: '/practice',
    featureName: 'practice_home',
    featureGroup: 'coaching',
    description: 'Practice tab — next up drill and My Practice'
  },
  {
    route: '/practice/:exerciseId',
    featureName: 'practice_exercise',
    featureGroup: 'coaching',
    description: 'Practice a specific exercise'
  },

  // Analytics & Stats
  {
    route: '/search',
    featureName: 'search',
    featureGroup: 'navigation',
    description: 'Global search'
  },
  {
    route: '/analytics/course-records',
    featureName: 'analytics_course_records',
    featureGroup: 'performance',
    description: 'Course records analytics'
  },
  {
    route: '/progression',
    featureName: 'progression',
    featureGroup: 'performance',
    description: 'Member progression and club activity'
  },

  // Services & Bookings
  {
    route: '/book-tee-time',
    featureName: 'tee_time_book',
    featureGroup: 'bookings',
    description: 'Book a tee time'
  },
  {
    route: '/book-tee-time/:clubId',
    featureName: 'tee_time_book_club',
    featureGroup: 'bookings',
    description: 'Book a tee time at a specific club'
  },
  {
    route: '/services',
    featureName: 'member_services',
    featureGroup: 'bookings',
    description: 'Member services (carts, lockers, etc.)'
  },
  {
    route: '/services/:clubId',
    featureName: 'member_services_club',
    featureGroup: 'bookings',
    description: 'Member services at a specific club'
  }
];

/**
 * Get feature name from route path
 * Handles dynamic routes with parameters (e.g., :eventId, :memberId)
 */
export function getFeatureFromRoute(routePath: string): { featureName: string; featureGroup: string } | null {
  // Direct match first
  const directMatch = MEMBER_FEATURE_MAPPING.find(mapping => mapping.route === routePath);
  if (directMatch) {
    return {
      featureName: directMatch.featureName,
      featureGroup: directMatch.featureGroup
    };
  }

  // Pattern matching for dynamic routes
  for (const mapping of MEMBER_FEATURE_MAPPING) {
    const pattern = mapping.route.replace(/:[\w]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    if (regex.test(routePath)) {
      return {
        featureName: mapping.featureName,
        featureGroup: mapping.featureGroup
      };
    }
  }

  return null;
}

/**
 * Get all features grouped by category
 */
export function getFeaturesByGroup(): Record<string, FeatureMapping[]> {
  return MEMBER_FEATURE_MAPPING.reduce((groups, mapping) => {
    if (!groups[mapping.featureGroup]) {
      groups[mapping.featureGroup] = [];
    }
    groups[mapping.featureGroup].push(mapping);
    return groups;
  }, {} as Record<string, FeatureMapping[]>);
}

/**
 * Get list of all unique feature names
 */
export function getAllFeatureNames(): string[] {
  return [...new Set(MEMBER_FEATURE_MAPPING.map(mapping => mapping.featureName))];
}

/**
 * Get list of all feature groups
 */
export function getAllFeatureGroups(): string[] {
  return [...new Set(MEMBER_FEATURE_MAPPING.map(mapping => mapping.featureGroup))];
}
